/**
 * Campus API
 * Handles campus management operations
 *
 * Endpoints:
 * - GET /api/campuses/ - List all campuses (with optional filters)
 * - POST /api/campuses/ - Create new campus
 * - GET /api/campuses/{id}/ - Get campus details
 * - PATCH /api/campuses/{id}/ - Update campus
 * - DELETE /api/campuses/{id}/ - Delete campus
 * - POST /api/campuses/{id}/activate/ - Activate campus
 * - POST /api/campuses/{id}/deactivate/ - Deactivate campus
 */

import apiClient from './client'
import { buildJsonApiQuery } from '@/utils/jsonapi'
import { TIMEZONE_GROUPS } from '@/utils/timezones'

/**
 * Get all campuses with optional filters
 *
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { is_active: true, name: { icontains: 'main' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields
 * @param {Array<string>|string} [queryOptions.include] - Relations to include
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 * @returns {Promise<Object>} JSON:API response with data, meta, links
 * @example
 * {
 *   data: [
 *     {
 *       id: "1",
 *       name: "North Campus",
 *       location: "123 Main St, City, ST 12345",
 *       timezone: "America/Chicago",
 *       is_active: true,
 *       created_at: "2025-10-12T18:00:00Z",
 *       updated_at: "2025-10-12T18:00:00Z"
 *     }
 *   ]
 * }
 */
export async function getCampuses(queryOptions = {}) {
  const params = buildJsonApiQuery(queryOptions)
  const response = await apiClient.get('/campuses/', { params })
  return response.data
}

/**
 * Get single campus by ID
 *
 * @param {string} id - Campus ID
 * @returns {Promise<Object>} Campus details
 */
export async function getCampus(id) {
  const response = await apiClient.get(`/campuses/${id}/`)

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Create new campus
 *
 * @param {Object} campusData - Campus data
 * @param {string} campusData.name - Campus name (required)
 * @param {string} [campusData.location] - Physical address
 * @param {string} campusData.timezone - IANA timezone (required)
 * @param {boolean} [campusData.is_active=true] - Active status
 * @returns {Promise<Object>} Created campus
 */
export async function createCampus(campusData) {
  // Format as JSON:API request
  const payload = {
    data: {
      type: 'campus',
      attributes: campusData
    }
  }

  const response = await apiClient.post('/campuses/', payload)

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Update campus
 *
 * @param {string} id - Campus ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.name] - Campus name
 * @param {string} [updates.location] - Physical address
 * @param {string} [updates.timezone] - IANA timezone
 * @param {boolean} [updates.is_active] - Active status
 * @returns {Promise<Object>} Updated campus
 */
export async function updateCampus(id, updates) {
  // Format as JSON:API request
  const payload = {
    data: {
      type: 'campus',
      id: id,
      attributes: updates
    }
  }

  const response = await apiClient.patch(`/campuses/${id}/`, payload)

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Delete campus
 * Note: Will fail with 400 if campus has dependencies (users, data)
 *
 * @param {string} id - Campus ID
 * @returns {Promise<void>}
 * @throws {Error} If campus has dependencies
 */
export async function deleteCampus(id) {
  await apiClient.delete(`/campuses/${id}/`)
}

/**
 * Activate campus
 *
 * @param {string} id - Campus ID
 * @returns {Promise<Object>} Updated campus
 */
export async function activateCampus(id) {
  const response = await apiClient.post(`/campuses/${id}/activate/`)

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Deactivate campus
 *
 * @param {string} id - Campus ID
 * @returns {Promise<Object>} Updated campus
 */
export async function deactivateCampus(id) {
  const response = await apiClient.post(`/campuses/${id}/deactivate/`)

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Common US timezones for quick access
 */
export const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
]

/**
 * All available timezones grouped by region.
 * Single source of truth lives in @/utils/timezones (generated from the exact
 * set the API accepts); re-exported here for existing importers.
 */
export { TIMEZONE_GROUPS }

export default {
  getCampuses,
  getCampus,
  createCampus,
  updateCampus,
  deleteCampus,
  activateCampus,
  deactivateCampus,
  COMMON_TIMEZONES,
  TIMEZONE_GROUPS,
}
